import React from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { Upload } from 'src/components/upload';
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';
import { OptionModel } from 'src/types/api-response';
import { CaySam, CaySamNhatKy } from 'src/types/caysam';
import CustomLoadingButton from 'src/components/button/button-loadding-dark-model';
import useCaySamFilterOptions from '../../utils/use-caysam-filter-options';
import { useCaySam } from './hooks/use-losam';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  lotName: string;
  lotCode: string;
  gardenName: string;
  positionLabel: string;
  caySamData: CaySam | null;
  onUpdateCaySam: (caySamData: Partial<CaySam>) => void;
  loaiTuoiOptions: OptionModel[];
  diemSucKhoeOptions?: OptionModel[];
  loading: boolean;
  onSave: () => void;
};

// Helper function: Tạo nhật ký mới rỗng với các giá trị mặc định
const createEmptyNhatKy = (): CaySamNhatKy => ({
  id: '',
  caySamId: '',
  taiKhoanId: '',
  ngayGhi: new Date().toISOString(),
  soLa: 0,
  diemSucKhoe: 1, // Mặc định: 1
  tinhTrang: 1, // Mặc định: 1
  hinhAnhTongQuan: '',
  hinhAnhChiTiet: '',
  ghiChu: '',
  isThongBao: false,
  trongLuong: 0,
});

// Giá trị mặc định cho Cây Sâm mới (nếu caySamData là null)
const createDefaultCaySam = (loSamId: number, viTriTrongLo: string, maCaySam: string): CaySam => ({
  caySam_ID: '',
  loSam_ID: loSamId,
  viTriTrongLo,
  blockChain: null,
  maCaySam,
  tuoiCay_ID: 1,
  caySamNhatKys: [createEmptyNhatKy()],
  isNFC: false,
  caySam_DinhKems : []
});

export default function CaySamNewDrawer({
  open,
  onClose,
  title,
  lotName,
  lotCode,
  gardenName,
  positionLabel,
  caySamData,
  onUpdateCaySam,
  loaiTuoiOptions,
  diemSucKhoeOptions,
  loading,
  onSave,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { tinhTrangOptions, diemSucKhoeOptions: hookDiemOptions } = useCaySamFilterOptions();
  const mergedDiemOptions =
    diemSucKhoeOptions && diemSucKhoeOptions.length > 0 ? diemSucKhoeOptions : hookDiemOptions;
  const { addCaySam, loading: caySamLoading, error: caySamError } = useCaySam();
  
  // Lấy loSam_ID từ caySamData để sử dụng trong khởi tạo
  const loSamId = caySamData?.loSam_ID || 0; 
  
  // Tạo mã cây sâm tự động từ mã lô và vị trí
  const generatedMaCaySam = React.useMemo(
    () => `${lotCode}_${positionLabel}`,
    [lotCode, positionLabel]
  );
  
  // Lấy nhật ký đầu tiên (nhật ký cho cây sâm mới)
  const newNhatKy = React.useMemo(
    () => caySamData?.caySamNhatKys?.[0] || createEmptyNhatKy(),
    [caySamData?.caySamNhatKys]
  );
  
  
  React.useEffect(() => {
    if (open) {
      // Nếu không có dữ liệu (tạo mới) HOẶC nếu nhật ký không có (cần đảm bảo nhật ký tồn tại)
      if (!caySamData || !caySamData.caySamNhatKys || caySamData.caySamNhatKys.length === 0) {
        // Thiết lập dữ liệu cây sâm mặc định với nhật ký khởi tạo
        const defaultData = createDefaultCaySam(loSamId, positionLabel, generatedMaCaySam);
        onUpdateCaySam(defaultData);
      } else if (generatedMaCaySam && caySamData.maCaySam !== generatedMaCaySam) {
         // Cập nhật mã cây sâm nếu nó bị thiếu hoặc sai
         onUpdateCaySam({ maCaySam: generatedMaCaySam });
      }
    }
    // Ghi chú: loSamId (từ props/state bên ngoài) không thay đổi khi drawer mở/đóng
  }, [generatedMaCaySam, onUpdateCaySam, open, loSamId, positionLabel, caySamData]);


  // Hiển thị lỗi từ API
  React.useEffect(() => {
    if (caySamError) {
      enqueueSnackbar(caySamError, { variant: 'error' });
    }
  }, [caySamError, enqueueSnackbar]);


  // Helper function: Cập nhật nhật ký
  const updateNhatKy = React.useCallback(
    (updates: Partial<CaySamNhatKy>) => {
      // Đảm bảo rằng chúng ta luôn có một nhật ký để cập nhật
      const currentNhatKy = newNhatKy; 
      
      const updatedNhatKy = { ...currentNhatKy, ...updates };
      // Vì đây là luồng TẠO MỚI, chúng ta chỉ quan tâm đến nhật ký [0]
      const restNhatKys = caySamData?.caySamNhatKys?.slice(1) || [];
      onUpdateCaySam({ caySamNhatKys: [updatedNhatKy, ...restNhatKys] });
    },
    [caySamData?.caySamNhatKys, onUpdateCaySam, newNhatKy]
  );

  // Convert base64 to File (Giữ nguyên)
  const base64ToFile = React.useCallback((base64: string, filename: string): File | null => {
    try {
      if (!base64.startsWith('data:')) return null;

      const arr = base64.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const bstr = atob(arr[1]);
      const n = bstr.length;
      const u8arr = new Uint8Array(n);

      for (let i = 0; i < n; i += 1) {
        u8arr[i] = bstr.charCodeAt(i);
      }

      return new File([u8arr], filename, { type: mime });
    } catch (error) {
      console.error('Error converting base64 to file:', error);
      return null;
    }
  }, []);

  // Handle upload ảnh tổng quát (Giữ nguyên)
  const handleDropTongQuan = React.useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        updateNhatKy({ hinhAnhTongQuan: reader.result as string });
        enqueueSnackbar('Ảnh tổng quát đã được tải lên', { variant: 'success' });
      };
      reader.readAsDataURL(file);
    },
    [updateNhatKy, enqueueSnackbar]
  );

  // Handle upload ảnh chi tiết (Giữ nguyên)
  const handleDropChiTiet = React.useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        updateNhatKy({ hinhAnhChiTiet: reader.result as string });
        enqueueSnackbar('Ảnh chi tiết đã được tải lên', { variant: 'success' });
      };
      reader.readAsDataURL(file);
    },
    [updateNhatKy, enqueueSnackbar]
  );

  // Handle xóa ảnh (Giữ nguyên)
  const handleDeleteTongQuan = React.useCallback(() => {
    updateNhatKy({ hinhAnhTongQuan: '' });
  }, [updateNhatKy]);

  const handleDeleteChiTiet = React.useCallback(() => {
    updateNhatKy({ hinhAnhChiTiet: '' });
  }, [updateNhatKy]);

  // Handle lưu cây sâm mới
  const handleSaveNewCaySam = React.useCallback(async () => {
    // newNhatKy đã được khởi tạo mặc định, nên không cần kiểm tra null
    
    // Validation
    if (!caySamData) {
      enqueueSnackbar('Không có dữ liệu cây sâm', { variant: 'error' });
      return;
    }

    if (!generatedMaCaySam?.trim()) {
      enqueueSnackbar('Không thể tạo mã cây sâm', { variant: 'error' });
      return;
    }

    if (!caySamData.tuoiCay_ID) {
      enqueueSnackbar('Vui lòng chọn tuổi cây', { variant: 'error' });
      return;
    }

    // Loại bỏ các kiểm tra không cần thiết do giá trị mặc định là 1 đã được thiết lập
    // if (!newNhatKy?.tinhTrang) { ... }
    // if (!newNhatKy?.diemSucKhoe) { ... }
    
    try {
      // Chuẩn bị dữ liệu
      const addCaySamData = {
        CaySam: {
          loSam_ID: caySamData.loSam_ID || 0,
          viTriTrongLo: caySamData.viTriTrongLo || positionLabel,
          blockChain: null,
          maCaySam: generatedMaCaySam,
          tuoiCay_ID: caySamData.tuoiCay_ID,
        },
        CaySamNhatKy: {
          ngayGhi: new Date().toISOString(), 
          soLa: newNhatKy.soLa || 0,
          diemSucKhoe: newNhatKy.diemSucKhoe || 1,
          tinhTrang: newNhatKy.tinhTrang || 1,
        },
      };

      // Chuẩn bị files
      const files: File[] = [];
      if (newNhatKy.hinhAnhTongQuan) {
        const tongQuanFile = base64ToFile(newNhatKy.hinhAnhTongQuan, 'tongquan.jpg');
        if (tongQuanFile) files.push(tongQuanFile);
      }
      if (newNhatKy.hinhAnhChiTiet) {
        const chiTietFile = base64ToFile(newNhatKy.hinhAnhChiTiet, 'chitiet.jpg');
        if (chiTietFile) files.push(chiTietFile);
      }

      // Gọi API
      const result = await addCaySam(addCaySamData, files);

      if (result.messCode === 1) {
        enqueueSnackbar('Thêm cây sâm thành công!', { variant: 'success' });
        onSave();
      } else {
        enqueueSnackbar(result.message || 'Lỗi khi thêm cây sâm', { variant: 'error' });
      }
    } catch (error: any) {
      console.error('Error adding cây sâm:', error);
      enqueueSnackbar(error?.message || 'Lỗi khi thêm cây sâm', { variant: 'error' });
    }
  }, [
    caySamData,
    positionLabel,
    newNhatKy,
    generatedMaCaySam,
    addCaySam,
    base64ToFile,
    enqueueSnackbar,
    onSave,
  ]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 800 } } }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onClose} sx={{ mr: 1 }}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {title}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Stack spacing={3}>
            {/* Thông tin vị trí */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Thông tin vị trí
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Vườn trồng"
                  value={gardenName}
                  disabled
                  size="small"
                  InputProps={{
                    startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                  }}
                />
                <TextField
                  fullWidth
                  label="Lô sâm"
                  value={lotName}
                  disabled
                  size="small"
                  InputProps={{
                    startAdornment: <Iconify icon="solar:home-bold" sx={{ mr: 1 }} />,
                  }}
                />
                <TextField
                  fullWidth
                  label="Vị trí"
                  value={positionLabel}
                  disabled
                  size="small"
                  InputProps={{
                    startAdornment: <Iconify icon="solar:map-point-bold" sx={{ mr: 1 }} />,
                  }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Thông tin cây sâm */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Thông tin cây sâm
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Mã cây sâm"
                  value={generatedMaCaySam || ''}
                  required
                  size="small"
                  disabled
                  helperText="Mã cây sâm được tạo tự động từ mã lô và vị trí"
                  InputProps={{
                    startAdornment: (
                      <Iconify icon="solar:medal-ribbons-star-bold" sx={{ mr: 1 }} />
                    ),
                  }}
                />

                <FormControl fullWidth size="small" required>
                  <InputLabel>Tuổi cây</InputLabel>
                  <Select
                    value={caySamData?.tuoiCay_ID ?? 1}
                    label="Tuổi cây"
                    onChange={(e) => onUpdateCaySam({ tuoiCay_ID: Number(e.target.value) })}
                    startAdornment={<Iconify icon="solar:calendar-bold" sx={{ mr: 1 }} />}
                  >
                    {loaiTuoiOptions.map((opt) => (
                      <MenuItem key={opt.value} value={Number(opt.value)}>
                        {opt.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
 {/* Form fields */}
              <Stack spacing={2}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Tình trạng</InputLabel>
                  <Select
                    value={newNhatKy?.tinhTrang ?? 1}
                    label="Tình trạng"
                    onChange={(e) => updateNhatKy({ tinhTrang: Number(e.target.value) })}
                    startAdornment={<Iconify icon="solar:flag-2-bold" sx={{ mr: 1 }} />}
                  >
                    {tinhTrangOptions.map((opt) => (
                      <MenuItem key={opt.value} value={Number(opt.value)}>
                        {opt.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" required>
                  <InputLabel>Điểm sức khỏe</InputLabel>
                  <Select
                    value={newNhatKy?.diemSucKhoe ?? 1}
                    label="Điểm sức khỏe"
                    onChange={(e) => updateNhatKy({ diemSucKhoe: Number(e.target.value) })}
                    startAdornment={<Iconify icon="solar:check-circle-bold" sx={{ mr: 1 }} />}
                  >
                    {mergedDiemOptions?.map((opt) => (
                      <MenuItem key={opt.value} value={Number(opt.value)}>
                        {opt.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField 
                  fullWidth
                  label="Số lá"
                  type="number"
                  value={newNhatKy?.soLa ?? 0}
                  onChange={(e) => updateNhatKy({ soLa: Number(e.target.value) || 0 })}
                  size="small"
                  InputProps={{
                    startAdornment: <Iconify icon="solar:leaf-bold" sx={{ mr: 1 }} />,
                  }}
                />
              </Stack>
            <Divider />

            {/* Thông tin sinh trưởng */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Thông tin sinh trưởng ban đầu
              </Typography>

              {/* Upload ảnh */}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Ảnh tổng quát
                  </Typography>
                  <Upload
                    file={newNhatKy?.hinhAnhTongQuan || ''}
                    onDrop={handleDropTongQuan}
                    onDelete={handleDeleteTongQuan}
                    helperTitle = 'Ảnh chụp tổng thể'
                    thumbnail
                    helperText={
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          mx: 'auto',
                          display: 'block',
                          textAlign: 'center',
                          color: 'text.disabled',
                        }}
                      >
                        *.jpeg, *.jpg, *.png, *.gif
                      </Typography>
                    }
                    sx={{
                      '& .upload-zone': {
                        height: 140,
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Ảnh chi tiết
                  </Typography>
                  <Upload
                    file={newNhatKy?.hinhAnhChiTiet || ''}
                    onDrop={handleDropChiTiet}
                    onDelete={handleDeleteChiTiet}
                    helperTitle = 'Ảnh chụp cận cảnh'
                    thumbnail
                    helperText={
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          mx: 'auto',
                          display: 'block',
                          textAlign: 'center',
                          color: 'text.disabled',
                        }}
                      >
                        *.jpeg, *.jpg, *.png, *.gif
                      </Typography>
                    }
                    sx={{
                      '& .upload-zone': {
                        height: 140,
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              </Stack>

             
            </Box>

            <Divider />

            {/* Thông tin bổ sung */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Thông tin bổ sung
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.neutral',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Cây sâm mới sẽ được tạo với nhật ký sinh trưởng đầu tiên
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={onClose} fullWidth>
              Hủy
            </Button>
            <CustomLoadingButton
              variant="contained"
              onClick={handleSaveNewCaySam}
              loading={loading || caySamLoading}
              fullWidth
            > 
              Thêm mới
            </CustomLoadingButton>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}